import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import {
  useTrainings,
  useTrainingModules,
  useTrainingTopics,
  useTrainingContents,
} from '../../../api/hooks';
import { useGroups } from '../../../api/hooks/useGroups';
import { useBatchUpdateProgressByGroup } from '../../../api/hooks/useTrainingProgress';

interface GroupUpdateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const GroupUpdateModal: React.FC<GroupUpdateModalProps> = ({ open, onClose, onSuccess }) => {
  const { selectedOrganization } = useOrganization();
  const [selectedTrainingId, setSelectedTrainingId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedModuleId, setSelectedModuleId] = useState('');
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [selectedContentId, setSelectedContentId] = useState('');
  const [isCompleted, setIsCompleted] = useState(true);
  const [score, setScore] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  // Fetch trainings
  const { data: trainings = [], isLoading: trainingsLoading } = useTrainings(
    selectedOrganization?.id || '',
    { isActive: true }
  );

  // Fetch groups
  const { data: groups = [], isLoading: groupsLoading } = useGroups(
    selectedOrganization?.id || '',
    { isActive: true }
  );

  // Fetch modules for selected training
  const { data: modules = [], isLoading: modulesLoading } = useTrainingModules(
    selectedOrganization?.id || '',
    selectedTrainingId,
    { isActive: true }
  );

  // Fetch topics for selected module
  const { data: topics = [], isLoading: topicsLoading } = useTrainingTopics(
    selectedOrganization?.id || '',
    selectedTrainingId,
    selectedModuleId,
    { isActive: true }
  );

  // Fetch contents for selected topic
  const { data: contents = [], isLoading: contentsLoading } = useTrainingContents(
    selectedOrganization?.id || '',
    selectedTrainingId,
    selectedModuleId,
    selectedTopicId,
    { isActive: true }
  );

  // Batch update mutation
  const batchUpdateMutation = useBatchUpdateProgressByGroup(selectedOrganization?.id || '');

  // Reset dependent fields when parent changes
  useEffect(() => {
    setSelectedModuleId('');
    setSelectedTopicId('');
    setSelectedContentId('');
  }, [selectedTrainingId]);

  useEffect(() => {
    setSelectedTopicId('');
    setSelectedContentId('');
  }, [selectedModuleId]);

  useEffect(() => {
    setSelectedContentId('');
  }, [selectedTopicId]);

  // Reset all fields when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedTrainingId('');
      setSelectedGroupId('');
      setSelectedModuleId('');
      setSelectedTopicId('');
      setSelectedContentId('');
      setIsCompleted(true);
      setScore('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!selectedGroupId) {
      setError('Please select a group');
      return;
    }

    if (!selectedContentId) {
      setError('Please select a content item');
      return;
    }

    try {
      await batchUpdateMutation.mutateAsync({
        groupId: selectedGroupId,
        data: {
          contentId: selectedContentId,
          isCompleted,
          score: score !== '' ? Number(score) : undefined,
        },
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update progress');
    }
  };

  const isFormValid = selectedGroupId && selectedContentId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Group Progress Update</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Update progress for all organizations in a group for a specific content item.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          {/* Training Selection */}
          <FormControl fullWidth>
            <InputLabel>Training</InputLabel>
            <Select
              value={selectedTrainingId}
              onChange={(e) => setSelectedTrainingId(e.target.value)}
              label="Training"
              disabled={trainingsLoading}
            >
              {trainings.map((training) => (
                <MenuItem key={training.id} value={training.id}>
                  {training.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Group Selection */}
          <FormControl fullWidth>
            <InputLabel>Group</InputLabel>
            <Select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              label="Group"
              disabled={groupsLoading}
            >
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Module Selection */}
          {selectedTrainingId && (
            <FormControl fullWidth>
              <InputLabel>Module</InputLabel>
              <Select
                value={selectedModuleId}
                onChange={(e) => setSelectedModuleId(e.target.value)}
                label="Module"
                disabled={modulesLoading || modules.length === 0}
              >
                {modules.map((module) => (
                  <MenuItem key={module.id} value={module.id}>
                    {module.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Topic Selection */}
          {selectedModuleId && (
            <FormControl fullWidth>
              <InputLabel>Topic</InputLabel>
              <Select
                value={selectedTopicId}
                onChange={(e) => setSelectedTopicId(e.target.value)}
                label="Topic"
                disabled={topicsLoading || topics.length === 0}
              >
                {topics.map((topic) => (
                  <MenuItem key={topic.id} value={topic.id}>
                    {topic.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Content Selection */}
          {selectedTopicId && (
            <FormControl fullWidth>
              <InputLabel>Content</InputLabel>
              <Select
                value={selectedContentId}
                onChange={(e) => setSelectedContentId(e.target.value)}
                label="Content"
                disabled={contentsLoading || contents.length === 0}
              >
                {contents.map((content) => (
                  <MenuItem key={content.id} value={content.id}>
                    {content.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Completion Status */}
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={isCompleted ? 'completed' : 'incomplete'}
              onChange={(e) => setIsCompleted(e.target.value === 'completed')}
              label="Status"
            >
              <MenuItem value="completed">Mark as Completed</MenuItem>
              <MenuItem value="incomplete">Mark as Incomplete</MenuItem>
            </Select>
          </FormControl>

          {/* Score (Optional) */}
          {isCompleted && (
            <TextField
              label="Score (Optional)"
              type="number"
              value={score}
              onChange={(e) => setScore(e.target.value ? Number(e.target.value) : '')}
              fullWidth
              inputProps={{ min: 0, max: 100 }}
              helperText="Enter a score between 0 and 100"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={batchUpdateMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || batchUpdateMutation.isPending}
          startIcon={batchUpdateMutation.isPending ? <CircularProgress size={16} /> : null}
        >
          Update Group Progress
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupUpdateModal;

